"use client";

import './interactive.css';
import { __rest } from '../../_virtual/_tslib.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { useId, useState } from 'react';
import styles from './interactive.module.scss.js';
import getOS from '../../utilities/check-os/index.js';
import checkIfMobileDevice from '../../utilities/check-device/index.js';
import getBrowser from '../../utilities/check-browser/index.js';
import calculateRem from '../../utilities/calculate-rem/index.js';
import '../../utilities/use-viewport/index.js';
import calculateAspectRatio from '../../utilities/calculate-aspect-ratio/index.js';
import { checkIfCustomPadding } from './utils/check-if-custom-padding/index.js';
import { checkBackgroundKind } from './utils/check-background-kind/index.js';
import { validateSurface } from '../../globals.js';

const InteractiveTileContainer = (_a) => {
    var { height, width = '100%', aspectRatio = '1/1', background = 'lightSecondary', imageFallbackColor = 'lightSecondary', padding = calculateRem('16px'), surface: surfaceProp = 'lightPrimary', showBorder = false, dropShadow = 'none', id, children, role, onClick, href, tabIndex = 0, borderRadius = 'tile', focusBorderPosition = 'outside', ariaLabel, 'aria-label': ariaLabelNative, target, grouped, 'data-testid': testID = 'tile-container', 'data-track': track, 'data-track-ignore': trackIgnore, 'data-analyticstrack': analyticstrack, 'data-clickstream': clickstream, elementSurface } = _a, rest = __rest(_a, ["height", "width", "aspectRatio", "background", "imageFallbackColor", "padding", "surface", "showBorder", "dropShadow", "id", "children", "role", "onClick", "href", "tabIndex", "borderRadius", "focusBorderPosition", "ariaLabel", 'aria-label', "target", "grouped", 'data-testid', 'data-track', 'data-track-ignore', 'data-analyticstrack', 'data-clickstream', "elementSurface"]);
    const mobile = checkIfMobileDevice() || false;
    const android = getOS() === 'android';
    const ios = getOS() === 'ios';
    const chrome = getBrowser() === 'chrome';
    const safari = getBrowser() === 'safari';
    const surface = validateSurface(surfaceProp);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const tileContainerId = id || useId();
    const [active, setActive] = useState(false);
    const backgroundKind = checkBackgroundKind(background);
    const imageFallbackColorKind = checkBackgroundKind(imageFallbackColor);
    const isCustomPadding = checkIfCustomPadding(padding);
    const tileStyles = [
        styles.tileContainer,
        styles[`surface-${surface.toLowerCase()}`],
    ];
    const wrapperStyles = [styles.wrapper];
    const outterButtonStyles = [
        styles.outerButton,
        styles[`surface-${surface.toLowerCase()}`],
        styles[`focus-${focusBorderPosition}`],
    ];
    const innerTileContainerStyles = [styles.innerTileContainer];
    let backgroundToken;
    if (backgroundKind === 'tokenColor') {
        backgroundToken = background;
    }
    if (backgroundKind === 'imageSource' &&
        imageFallbackColorKind === 'tokenColor') {
        backgroundToken = imageFallbackColor;
    }
    /* istanbul ignore else -- @preserve */
    if (backgroundToken) {
        tileStyles.push(styles[`background-${backgroundToken.toLowerCase()}`]);
        outterButtonStyles.push(styles[`background-${backgroundToken.toLowerCase()}`]);
    }
    else if (elementSurface) {
        outterButtonStyles.push(styles[`background-${elementSurface.toLowerCase()}`]);
    }
    /* istanbul ignore if -- @preserve */
    if (!isCustomPadding) {
        tileStyles.push(styles[`spacing-${padding.toLowerCase()}`]);
    }
    /* istanbul ignore if -- @preserve */
    if (showBorder)
        tileStyles.push(styles.showBorder);
    /* istanbul ignore if -- @preserve */
    if (dropShadow !== 'none' && surface.startsWith('light')) {
        /* istanbul ignore if -- @preserve */
        if (dropShadow === 'subtle' && background === 'lightPrimary') {
            tileStyles.push(styles[`shadow-subtle-lightprimary`]);
        }
        /* istanbul ignore if -- @preserve */
        if (dropShadow === 'medium' && background === 'lightPrimary') {
            tileStyles.push(styles[`shadow-medium-lightprimary`]);
        }
        /* istanbul ignore if -- @preserve */
        if (dropShadow === 'medium' &&
            background &&
            !background.startsWith('light')) {
            tileStyles.push(styles[`shawdow-medium-dark`]);
        }
    }
    if (borderRadius === 'tile' || borderRadius === 'hero') {
        wrapperStyles.push(styles[borderRadius]);
        tileStyles.push(styles[borderRadius]);
        outterButtonStyles.push(styles[borderRadius]);
    }
    else {
        outterButtonStyles.push(styles.custom);
    }
    /* istanbul ignore if -- @preserve */
    if (mobile) {
        tileStyles.push(styles.mobile);
        wrapperStyles.push(styles.mobile);
    }
    /* istanbul ignore if -- @preserve */
    if (android)
        tileStyles.push(styles.android);
    if (active)
        wrapperStyles.push(styles.active);
    const _onClick = (e) => {
        /* istanbul ignore else -- @preserve */
        if (onClick) {
            onClick(e);
        }
        else if (href) {
            window.open(href, target);
        }
    };
    const _onKeyDown = (e) => {
        /* istanbul ignore else -- @preserve */
        if (e.key === 'Enter' || e.key === ' ') {
            if (e.key === ' ')
                e.preventDefault();
            const tc = document && document.getElementById(`OuterButton-${tileContainerId}`);
            /* istanbul ignore else -- @preserve */
            if (tc && tc.id === e.target.id)
                _onClick(e);
        }
    };
    const _onMouseDown = (e) => {
        const element = document && document.getElementById(`OuterButton-${tileContainerId}`);
        /* istanbul ignore if -- @preserve */
        if (!element)
            return;
        /* istanbul ignore else -- @preserve */
        if (element && element.id === e.target.id && !active) {
            setActive(true);
        }
    };
    const _onMouseUp = () => {
        /* istanbul ignore else -- @preserve */
        if (active)
            setActive(false);
    };
    const calculateRole = () => {
        if (role)
            return role;
        if (href)
            return 'link';
        /* istanbul ignore else -- @preserve */
        if (onClick)
            return 'button';
    };
    return (jsxs("div", { "data-testid": `${testID}-wrapper`, style: Object.assign({ height,
            width }, (borderRadius !== 'tile' &&
            borderRadius !== 'hero' && {
            borderRadius,
            '--custom-focus-borderRadius': `calc(${borderRadius} + 2px)`,
        })), className: wrapperStyles.join(' '), children: [jsx("div", Object.assign({ "data-testid": `${testID}-outer-button`, className: outterButtonStyles.join(' '), role: calculateRole(), onClick: _onClick, "aria-label": ariaLabel || ariaLabelNative, tabIndex: tabIndex }, (!!href && { target }), { onKeyDown: _onKeyDown, id: `OuterButton-${tileContainerId}`, "data-track": track, "data-track-ignore": trackIgnore, "data-analyticstrack": analyticstrack, "data-clickstream": clickstream, onMouseDown: _onMouseDown, onMouseUp: _onMouseUp })), jsx("div", Object.assign({}, rest, { "data-testid": `${testID}-tile`, id: tileContainerId, className: tileStyles.join(' '), style: Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({ height: '100%', width: '100%' }, (backgroundKind === 'imageSource' && {
                    backgroundImage: `url(${background})`,
                    backgroundColor: imageFallbackColorKind === 'custom'
                        ? imageFallbackColor
                        : undefined,
                })), (backgroundKind === 'custom' && { background })), (borderRadius !== 'tile' &&
                    borderRadius !== 'hero' && {
                    borderRadius,
                })), { aspectRatio: height ? undefined : calculateAspectRatio(aspectRatio) }), (isCustomPadding && { padding })), children: jsx("div", { "data-testid": `tile-inner-container-${testID}`, id: `tile-inner-container-${tileContainerId}`, className: innerTileContainerStyles.join(' '), style: {
                        height: 
                        /* istanbul ignore next -- @preserve */
                        grouped && (safari || (ios && chrome))
                            ? `calc(100% - ${padding} - ${padding} - ${showBorder ? calculateRem('2px') : '0px'}`
                            : undefined,
                    }, children: children }) }))] }));
};

export { InteractiveTileContainer as default };
